/*
 * Copyright 2026 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  type BackstageIdentityApi,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  type OAuthApi,
  oauthRequestApiRef,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';

const keycloakAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({
  id: 'ander.auth.keycloak',
});

export const appModuleKeycloakSignIn = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'keycloak-auth',
      params: defineParams =>
        defineParams({
          api: keycloakAuthApiRef,
          deps: {
            configApi: configApiRef,
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
          },
          factory: ({ configApi, discoveryApi, oauthRequestApi }) =>
            OAuth2.create({
              configApi,
              discoveryApi,
              oauthRequestApi,
              provider: {
                id: 'oidc',
                title: 'Keycloak',
                icon: () => null,
              },
              environment: configApi.getOptionalString('auth.environment'),
              defaultScopes: ['openid', 'profile', 'email'],
            }),
        }),
    }),
    SignInPageBlueprint.make({
      params: {
        loader: async () => props =>
          (
            <SignInPage
              {...props}
              providers={[
                {
                  id: 'keycloak-auth-provider',
                  title: 'Keycloak',
                  message: 'Sign in using Keycloak',
                  apiRef: keycloakAuthApiRef,
                },
              ]}
              title="Sign in to Backstage"
              align="center"
            />
          ),
      },
    }),
  ],
});
